import type { Block } from '@kioskforge/shared';
import { clockFace, countdownRemaining, type ClockWidget } from './clock';

/**
 * X3.2 (frontend half) — the clock widget's React block, rendering all six
 * declared styles (`CLOCK_STYLES`: digital, analog, flip, minimal, word).
 *
 * Preview semantics: a faithful still, like the other preview blocks. The time
 * is frozen (`clock.ts` `CLOCK_SAMPLE_ISO`) so the picture is stable for review
 * and for A's golden-image test — the player's interactive half (f10) ticks.
 *
 * `tablet_only` until A's server SVG renderer lands; the gallery badge and the
 * capability table (`packages/layout` `ESP32_UNSUPPORTED`) carry that, not this
 * component.
 */
export function ClockWidgetView({ block }: { block: ClockWidget }) {
  const face = clockFace(block);
  const style = block.props.style;

  return (
    <div className={`preview-clock preview-clock-${style}`} data-clock-style={style}>
      {style === 'analog' ? (
        <AnalogFace face={face} />
      ) : style === 'word' ? (
        <p className="preview-clock-words">{face.words}</p>
      ) : (
        <>
          <p className="preview-clock-time mono">
            {face.time}
            {block.props.show_seconds ? <span className="preview-clock-seconds">:{face.seconds}</span> : null}
            {face.meridiem ? <span className="preview-clock-meridiem">{face.meridiem}</span> : null}
          </p>
          {block.props.show_date ? <p className="preview-clock-date mono">{face.date}</p> : null}
        </>
      )}

      {block.props.world_clocks.length > 0 ? (
        <ul className="preview-clock-world">
          {block.props.world_clocks.map((zone) => (
            <li key={`${zone.label}-${zone.timezone}`} className="mono">
              <span>{zone.label}</span>
              <span>{zone.timezone}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {block.props.countdown ? (
        <p className="preview-clock-countdown mono">
          {block.props.countdown.label} · {countdownRemaining(block.props.countdown.to)}
        </p>
      ) : null}

      {block.props.show_sun ? (
        <p className="preview-clock-sun mono">SUNRISE / SUNSET</p>
      ) : null}
    </div>
  );
}

/** Analog face: hour/minute hands from the deterministic face angles. */
function AnalogFace({ face }: { face: ReturnType<typeof clockFace> }) {
  return (
    <div className="preview-clock-analog" role="img" aria-label={`Analog clock, ${face.time}`}>
      <span className="preview-clock-tick preview-clock-tick-12" />
      <span className="preview-clock-tick preview-clock-tick-3" />
      <span className="preview-clock-tick preview-clock-tick-6" />
      <span className="preview-clock-tick preview-clock-tick-9" />
      <span className="preview-clock-hand preview-clock-hour" style={{ transform: `translateX(-50%) rotate(${face.hands.hour}deg)` }} />
      <span className="preview-clock-hand preview-clock-minute" style={{ transform: `translateX(-50%) rotate(${face.hands.minute}deg)` }} />
      <span className="preview-clock-hub" />
    </div>
  );
}
